<template>
  <div class="w-full">
    <div class="result-list-container flex flex-col overflow-y-scroll bg-white">
      <div class="flex flex-col gap-3 border-b border-gray-400 px-6 py-8">
        <Headline tag="h4" look="h5" weight="normal">
          {{ $t('store_finder.title') }}
        </Headline>
        <StoreFinderSearch />
      </div>
      <div v-if="!fetching">
        <div
          v-for="newOpeningStore in storesPartitioned.newOpenings"
          :id="`store-${newOpeningStore.id}`"
          :key="newOpeningStore.id"
        >
          <StoreFinderResultNewOpening :store="newOpeningStore" />
        </div>
        <div
          v-for="regularStore in storesPartitioned.regular"
          :id="`store-${regularStore.id}`"
          :key="regularStore.id"
        >
          <StoreFinderResult :store="regularStore" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStores } from '~/composables/useStores'
import { Action } from '~/plugins/toast'
import type {
  IStore,
  IStoreActiveTeaser,
  StoresParams,
} from '~/rpcMethods/stores'

const {
  stores,
  fetching,
  fetch: fetchStores,
  search,
  searchOption,
} = useStores()
const { logger } = useLogging()

const { data: geocodeData, fetch: fetchGeocode } = useGeocode()
const { resetMarkers, resetLatLngBounds } = useMapMarker()
const { alert } = useToast()
const route = useRoute()
const $i18n = useI18n()

onUnmounted(() => {
  resetMarkers()
  resetLatLngBounds()
})

try {
  search.value = decodeURIComponent(route.query.location as string)
  await fetchGeocode({ search: search.value })
  const searchParams: StoresParams = {
    lat: geocodeData.value?.results[0]?.geometry?.location
      ?.lat as unknown as number,
    lng: geocodeData.value?.results[0]?.geometry?.location
      ?.lng as unknown as number,
    geocodeResults: geocodeData.value?.results,
  }
  if (searchOption.value && searchOption.value !== 'ALL') {
    searchParams.type = searchOption.value
  }

  await fetchStores(searchParams)
} catch (error: unknown) {
  logger.error(error, {
    why: 'rpc:getStores',
    where: 'StoreFinderResults.vue',
  })
  alert($i18n.t('error.connection'), Action.error)
}

const storesPartitioned = computed(() =>
  stores.value.reduce(
    (prevVal, curVal) => {
      const activeTeaser =
        curVal.opening && getActiveStoreTeaser(curVal.opening.teasers)
      if (activeTeaser) {
        prevVal.newOpenings.push({ ...curVal, activeTeaser })
      } else {
        prevVal.regular.push(curVal)
      }
      return prevVal
    },
    { newOpenings: [], regular: [] } as {
      newOpenings: Array<IStoreActiveTeaser>
      regular: (IStore & { isRopo?: boolean })[]
    },
  ),
)
</script>

<style lang="scss" scoped>
.open::before {
  font-size: 30px;
  content: '\2022';
  color: #40d49b;
  margin-right: 4px;
}

.closed::before {
  font-size: 30px;
  content: '\2022';
  color: #f77171;
  margin-right: 4px;
}

.result-list-container {
  max-height: calc(100vh - 65px);
}
</style>
